/**
 * A task conversation's title: the requester's first message, written once when the
 * conversation is allocated (wayai#4640, decision 3). No model call — the title is what
 * the requester sent, normalized and truncated.
 *
 * The one rule, applied on every channel that reaches a task hub:
 *   - the message's text, when it has any;
 *   - else a voice note → `TASK_TITLE_VOICE`, which clients render as their localized
 *     "Voice message" label (`isVoiceTaskTitle`);
 *   - else an attachment → its file name;
 *   - else no title (`null`), and the row keeps its id-based label.
 */

/** Longest stored title, in code points, the truncation ellipsis included. */
export const TASK_TITLE_MAX_LENGTH = 100;

/**
 * The stored title of a task opened by a voice note. Readable as it stands in every
 * surface that shows the raw value (analytics, admin reads); clients that render titles
 * compare against it with `isVoiceTaskTitle` and show their own localized label instead.
 */
export const TASK_TITLE_VOICE = '[Voice message]';

/** The parts of a task's first message the title is derived from. */
export interface TaskOpeningMessage {
  /** The message's text as the requester sent it. */
  text?: string | null;
  /** True when the message is a voice note (an audio recording rather than typed text). */
  voice?: boolean;
  /** The first attachment's file name, when the message carries one. */
  fileName?: string | null;
}

// Whitespace, and the C0 and C1 control characters, each collapse into a single space.
// eslint-disable-next-line no-control-regex
const SEPARATOR = /[\s\u0000-\u001f\u007f-\u009f]/;

/**
 * `value` with every run of separators collapsed to one space and the ends trimmed, cut to
 * `TASK_TITLE_MAX_LENGTH` code points with an ellipsis, or '' when nothing is left. One pass
 * that stops once the title is full, so a multi-megabyte message costs no more than a short
 * one; by code point, so a surrogate pair (an emoji) is never split in half.
 */
function titleText(value: string | null | undefined): string {
  if (!value) return '';
  const chars: string[] = [];
  let pendingSpace = false;
  for (const char of value) {
    if (SEPARATOR.test(char)) {
      pendingSpace = chars.length > 0;
      continue;
    }
    if (pendingSpace) chars.push(' ');
    pendingSpace = false;
    chars.push(char);
    if (chars.length > TASK_TITLE_MAX_LENGTH) break;
  }
  if (chars.length <= TASK_TITLE_MAX_LENGTH) return chars.join('');
  return `${chars.slice(0, TASK_TITLE_MAX_LENGTH - 1).join('').trimEnd()}…`;
}

/** The title of a task whose first message is `opening`, or `null` when it has none. */
export function deriveTaskTitle(opening: TaskOpeningMessage): string | null {
  const text = titleText(opening.text);
  if (text) return text;
  if (opening.voice) return TASK_TITLE_VOICE;
  return titleText(opening.fileName) || null;
}

/** True when `title` is the stored title of a task opened by a voice note. */
export function isVoiceTaskTitle(title: string | null | undefined): boolean {
  return title === TASK_TITLE_VOICE;
}
